import React, {
  DetailedHTMLProps,
  FC,
  ImgHTMLAttributes,
  useMemo,
  useRef,
} from 'react';

import { useResizeObserver } from 'usehooks-ts';

import { SharpLoaderImage } from '@web/utils/sharp-loader';

function getImage(sortedImages: readonly SharpLoaderImage[], width: number) {
  let bestMatch = sortedImages.at(-1);
  for (const image of sortedImages) {
    if (image.width >= width) {
      bestMatch = image;
      break;
    }
  }
  return bestMatch;
}

function buildSrcSet(images: readonly SharpLoaderImage[], width: number) {
  const sortedImages = [...images].sort((i1, i2) => i1.width - i2.width);
  const srcSets: Array<{ srcSet: string; type: string }> = [];

  for (const format of ['webp', 'png', 'jpeg', 'gif']) {
    const filteredImages = sortedImages.filter(
      (img) => img.format === format && !img.url.startsWith('data:'),
    );

    const x1 = getImage(filteredImages, width);
    const x2 = getImage(filteredImages, width * 2);
    if (x1) {
      srcSets.push({
        srcSet: `${x1.url} 1x, ${x2?.url} 2x`,
        type: x1.type,
      });
    }
  }

  return srcSets;
}

export interface ISharpImageProps
  extends Omit<
    DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>,
    'src' | 'srcSet'
  > {
  src: readonly SharpLoaderImage[];
}

export const SharpImage: FC<ISharpImageProps> = ({ src, ...props }) => {
  const ref = useRef<HTMLImageElement>(null);
  const { width: renderedWidth } = useResizeObserver({ ref });
  const srcSet = useMemo(
    () => (renderedWidth ? buildSrcSet(src, renderedWidth) : null),
    [src, renderedWidth],
  );
  const blur = src.find((img) => img.url.startsWith('data:'));

  return (
    <picture>
      {srcSet?.map(({ srcSet, type }) => (
        <source key={type} srcSet={srcSet} type={type} />
      ))}

      <img
        {...props}
        ref={ref}
        alt={props.alt ?? ''}
        width={props.width ?? blur?.width}
        height={props.height ?? blur?.height}
        src={blur?.url}
      />
    </picture>
  );
};
